import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { useHasPermission } from "../../auth/useClaims";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetCollectionsSummaryQuery } from "../../collections/collectionsApi";
import { useDashboardPeriod } from "../useDashboardPeriod";
import { money } from "../dashboardHelpers";
import { CHART_COLORS } from "../chartColors";
import DashboardSectionCard from "../components/DashboardSectionCard";

// Collections is the one module with ~10 years of genuine history already
// in TexasCentral.Cheque_Collection (verified directly against the DB, not
// assumed) - this is the flagship widget for the comparison framework:
// driven by the global Period+Compare control instead of a hardcoded
// window, and the only widget that renders a true multi-year overlay
// (section 8) rather than a single current-vs-comparison line.
//
// Granularity adapts to the selected period's span - day-level for
// anything up to ~2 months, month-level beyond that (e.g. This Year) -
// using the same /collections/summary groupBy the widget always used,
// just with the bucket size chosen dynamically instead of hardcoded "day".
const bucketFor = (dateFrom, dateTo) => {
  const days = (new Date(dateTo) - new Date(dateFrom)) / 86400000;
  return days > 62 ? "month" : "day";
};

// Aligns a summary series by relative position (1st point, 2nd point, ...)
// rather than real calendar date, so a current-period line and a
// comparison-period line (different real dates) can share one x-axis.
const toIndexedSeries = (rows) =>
  (rows || [])
    .slice()
    .sort((a, b) => a.groupKey.localeCompare(b.groupKey))
    .map((r, i) => ({ index: i, label: r.groupLabel, amount: r.totalAmount }));

const mergeSeries = (base, overlays) => {
  const length = Math.max(base.length, ...overlays.map((o) => o.series.length));
  const merged = [];
  for (let i = 0; i < length; i++) {
    const row = { label: base[i]?.label ?? `#${i + 1}`, current: base[i]?.amount };
    overlays.forEach((o) => { row[o.key] = o.series[i]?.amount; });
    merged.push(row);
  }
  return merged;
};

const CollectionTrendChart = () => {
  const canView = useHasPermission("Collections.View");
  const { selectedBranchId } = useBranchFilter();
  const { period, comparison } = useDashboardPeriod();
  const branchId = selectedBranchId ?? undefined;
  const groupBy = bucketFor(period.dateFrom, period.dateTo);

  const { data: currentData, isFetching } = useGetCollectionsSummaryQuery(
    { branchId, dateFrom: period.dateFrom, dateTo: period.dateTo, groupBy },
    { skip: !canView }
  );

  // Multi-year: one query per prior year (section 8) - generic, not
  // special-cased for "today's 5 years" - it works the same way whether 2
  // or 10 years of real data exist behind it.
  const multiYearWindows = comparison?.type === "multi" ? comparison.windows : [];
  const multiYearQueries = [
    useGetCollectionsSummaryQuery(
      multiYearWindows[0] ? { branchId, dateFrom: multiYearWindows[0].dateFrom, dateTo: multiYearWindows[0].dateTo, groupBy } : undefined,
      { skip: !canView || !multiYearWindows[0] }
    ),
    useGetCollectionsSummaryQuery(
      multiYearWindows[1] ? { branchId, dateFrom: multiYearWindows[1].dateFrom, dateTo: multiYearWindows[1].dateTo, groupBy } : undefined,
      { skip: !canView || !multiYearWindows[1] }
    ),
    useGetCollectionsSummaryQuery(
      multiYearWindows[2] ? { branchId, dateFrom: multiYearWindows[2].dateFrom, dateTo: multiYearWindows[2].dateTo, groupBy } : undefined,
      { skip: !canView || !multiYearWindows[2] }
    ),
    useGetCollectionsSummaryQuery(
      multiYearWindows[3] ? { branchId, dateFrom: multiYearWindows[3].dateFrom, dateTo: multiYearWindows[3].dateTo, groupBy } : undefined,
      { skip: !canView || !multiYearWindows[3] }
    ),
    useGetCollectionsSummaryQuery(
      multiYearWindows[4] ? { branchId, dateFrom: multiYearWindows[4].dateFrom, dateTo: multiYearWindows[4].dateTo, groupBy } : undefined,
      { skip: !canView || !multiYearWindows[4] }
    ),
    useGetCollectionsSummaryQuery(
      multiYearWindows[5] ? { branchId, dateFrom: multiYearWindows[5].dateFrom, dateTo: multiYearWindows[5].dateTo, groupBy } : undefined,
      { skip: !canView || !multiYearWindows[5] }
    ),
    useGetCollectionsSummaryQuery(
      multiYearWindows[6] ? { branchId, dateFrom: multiYearWindows[6].dateFrom, dateTo: multiYearWindows[6].dateTo, groupBy } : undefined,
      { skip: !canView || !multiYearWindows[6] }
    ),
    useGetCollectionsSummaryQuery(
      multiYearWindows[7] ? { branchId, dateFrom: multiYearWindows[7].dateFrom, dateTo: multiYearWindows[7].dateTo, groupBy } : undefined,
      { skip: !canView || !multiYearWindows[7] }
    ),
    useGetCollectionsSummaryQuery(
      multiYearWindows[8] ? { branchId, dateFrom: multiYearWindows[8].dateFrom, dateTo: multiYearWindows[8].dateTo, groupBy } : undefined,
      { skip: !canView || !multiYearWindows[8] }
    ),
  ];

  const singleCompare = comparison?.type === "single" ? comparison : null;
  const { data: compareData, isFetching: isFetchingCompare } = useGetCollectionsSummaryQuery(
    singleCompare ? { branchId, dateFrom: singleCompare.dateFrom, dateTo: singleCompare.dateTo, groupBy } : undefined,
    { skip: !canView || !singleCompare }
  );

  if (!canView) return null;

  const currentSeries = toIndexedSeries(currentData);
  const hasMultiYear = multiYearWindows.length > 0;
  const hasSingleCompare = Boolean(singleCompare);

  let chartData = currentSeries.map((r) => ({ label: r.label, current: r.amount }));
  let overlayKeys = [];
  let compareHasData = false;

  if (hasSingleCompare) {
    const compareSeries = toIndexedSeries(compareData);
    compareHasData = compareSeries.some((r) => Number(r.amount) > 0);
    chartData = mergeSeries(currentSeries, [{ key: "compare", series: compareSeries }]);
    overlayKeys = [{ key: "compare", label: `vs ${singleCompare.label}` }];
  } else if (hasMultiYear) {
    const overlays = multiYearQueries
      .map((q, i) => ({ key: `y${i}`, label: multiYearWindows[i]?.label, series: toIndexedSeries(q.data) }))
      .filter((o) => o.label);
    compareHasData = overlays.some((o) => o.series.some((r) => Number(r.amount) > 0));
    chartData = mergeSeries(currentSeries, overlays);
    overlayKeys = overlays.map((o) => ({ key: o.key, label: o.label }));
  }

  const loading = isFetching || isFetchingCompare || multiYearQueries.some((q) => q.isFetching);

  return (
    <div className="col-12 col-xl-6">
      <DashboardSectionCard title="Collection Trend" headerRight={period.label} isLoading={loading} isEmpty={currentSeries.length === 0}>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => money(v)} />
            {overlayKeys.length > 0 && <Legend wrapperStyle={{ fontSize: 11 }} />}
            <Line type="monotone" dataKey="current" name={period.label} stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 3 }} />
            {overlayKeys.map((o, i) => (
              <Line
                key={o.key}
                type="monotone"
                dataKey={o.key}
                name={o.label}
                stroke={CHART_COLORS[(i + 1) % CHART_COLORS.length]}
                strokeWidth={1.5}
                strokeDasharray={hasMultiYear ? undefined : "4 3"}
                dot={{ r: 2 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
        {(hasSingleCompare || hasMultiYear) && !loading && !compareHasData && (
          <div className="small text-muted fst-italic mt-1">No comparison data yet for {comparison.label}</div>
        )}
      </DashboardSectionCard>
    </div>
  );
};

export default CollectionTrendChart;
